import { useCallback, useEffect, useState } from "react";
import { JSONTree } from "react-json-tree";
import { useLocation, useNavigate } from "react-router-dom";
import swal from "sweetalert";
import nmcLogoImg from "../assets/images/nmc_logo.png";
import RedFishImg from "../assets/images/redfish_logo_new.png";
import "../css/style.css";
import DataService from "../services/data.service";
import TokenService from "../services/token.service";
import { theme } from "../utils/constants";

const RedFishPage = () => {
  const [isRawSelected, setIsRawSelected] = useState(false);
  const [dataContent, setDataContent] = useState(null);
  const [dataString, setDataString] = useState("");
  const [emailDisplay, setEmailDisplay] = useState("");
  // Navigation hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Memoized error handler to avoid recreation on every render
  // Note: navigate from react-router is stable and won't cause infinite loops
  const handleError = useCallback(
    (error) => {
      if (error.response?.data?.message === "User logout") {
        swal(
          "Your session has expired!",
          "Please login again.",
          "warning"
        ).then(() => {
          TokenService.removeUser();
          localStorage.clear();
          navigate("/");
        });
      } else if (error.response) {
        console.error("Server error:", error.response.data);
        swal(
          "Error",
          error.response.data?.message || "An error occurred",
          "error"
        );
      } else if (error.request) {
        console.error("Network error:", error.request);
        swal(
          "Network Error",
          "Unable to connect to the server. Please check your connection.",
          "error"
        );
      } else {
        console.error("Error:", error.message);
        swal("Error", error.message || "An unexpected error occurred", "error");
      }
    },
    [navigate]
  );

  // Effect for setting data string when data content changes
  useEffect(() => {
    if (dataContent) {
      setDataString(JSON.stringify(dataContent));
    }
  }, [dataContent]);

  // Effect for getting user email on mount (runs only once)
  useEffect(() => {
    if (localStorage.getItem("email")) {
      setEmailDisplay(JSON.parse(localStorage.getItem("email")));
    }
  }, []);

  // Effect for fetching data on first load and when path changes
  useEffect(() => {
    DataService.getData(location.pathname, TokenService.getToken())
      .then((response) => {
        console.log(response);
        setDataContent(response.data);
      })
      .catch((error) => {
        console.log(error.response);
        handleError(error);
      });
    // handleError is stable (memoized with useCallback) and navigate from react-router is stable
  }, [location.pathname, handleError]);

  // Function to handle link selection
  const selectLink = (displayValue) => {
    let pathDisplayValue = displayValue + "/";

    if (location.pathname !== pathDisplayValue) {
      navigate(pathDisplayValue);
    }
  };

  // Function to decide which text will be a clickable link
  const valueRenderer = (raw, value, keyPath, keyPath2) => {
    if (
      keyPath === "@odata.id" ||
      keyPath === "@Redfish.ActionInfo" ||
      (keyPath2 && String(keyPath2).includes("Links"))
    ) {
      return (
        <span className="links" onClick={() => selectLink(value)}>
          {value}
        </span>
      );
    }
    return value;
  };

  // Function to handle user logout
  const handleLogout = () => {
    TokenService.removeUser();
    localStorage.clear();
    swal("Logout success!", "", "success");
    navigate("/");
  };

  return (
    <>
      <div className="nav-bar">
        {" "}
        <img
          className="logo"
          src={nmcLogoImg}
          alt="nmc-logo"
          onClick={() => navigate("/")}
        />
        <div className="user-email">{emailDisplay}</div>
        <div className="logout-btn" onClick={() => handleLogout()}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-box-arrow-left"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M6 12.5a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v2a.5.5 0 0 1-1 0v-2A1.5 1.5 0 0 1 6.5 2h8A1.5 1.5 0 0 1 16 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 5 12.5v-2a.5.5 0 0 1 1 0v2z"
            />
            <path
              fillRule="evenodd"
              d="M.146 8.354a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L1.707 7.5H10.5a.5.5 0 0 1 0 1H1.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3z"
            />
          </svg>{" "}
          <div className="ml-1">Logout</div>
        </div>
      </div>
      <div className="redfish-page-main">
        <div className="json-container col-sm-8 col-md-8 col-lg-7 col-xl-6 ">
          <div className="redfish-img-wrap">
            {" "}
            <img className="redfish-img" src={RedFishImg} alt="redfish-logo" />
          </div>
          <div className="toggle-button-wrap">
            {" "}
            <div className="toggle-button">
              <button
                className={`toggle-btn ${!isRawSelected ? "selected" : ""}`}
                onClick={() => setIsRawSelected(false)}
              >
                Parsed
              </button>
              <button
                className={`toggle-btn ${isRawSelected ? "selected" : ""}`}
                onClick={() => setIsRawSelected(true)}
              >
                Raw
              </button>
            </div>
          </div>
          {!isRawSelected && (
            <JSONTree
              data={dataContent}
              theme={{
                extend: theme,
                valueLabel: {},
                nestedNodeLabel: ({ style }, keyPath, nodeType, expanded) => ({
                  style: {
                    ...style,
                    textTransform: expanded ? "uppercase" : style.textTransform,
                  },
                }),
              }}
              invertTheme={true}
              valueRenderer={(raw, value, ...rest) =>
                valueRenderer(raw, value, ...rest)
              }
              shouldExpandNodeInitially={(keyName, data, level) => true}
              hideRoot={true}
              getItemString={(type, data, itemType, itemString, keyPath) => {
                return <span>{"{" + itemString + "}"}</span>;
              }}
            />
          )}
          {isRawSelected && <div className="json-raw-wrap">{dataString}</div>}
        </div>
      </div>
    </>
  );
};

export default RedFishPage;
